import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bullseye, Spinner } from '@patternfly/react-core';
import { RouterParams } from '@routes/utils';
import { useApplicationBreadcrumbs } from '~/components/Applications/breadcrumbs/breadcrumb-utils';
import { DetailsPage } from '~/components/DetailsPage';
import { createDetailsPageAction } from '~/components/DetailsPage/utils';
import { StatusIconWithTextLabel } from '~/components/topology/StatusIcon';
import { PipelineRunLabel, runStatus } from '~/consts/pipelinerun';
import { CONFORMA_TASK, ROXCTL_SCAN_TASK } from '~/consts/security';
import { FeatureFlagIndicator } from '~/feature-flags/FeatureFlagIndicator';
import { usePipelineRunV2 } from '~/hooks/usePipelineRunsV2';
import { useStatusOnFavicon } from '~/hooks/useStatusOnFavicon';
import { useTaskRunV2 } from '~/hooks/useTaskRunsV2';
import {
  PIPELINERUN_DETAILS_PATH,
  PIPELINERUN_LIST_PATH,
  PIPELINERUN_TASK_LIST,
  TASKRUN_DETAILS_PATH,
} from '~/routes/paths';
import { useNamespace } from '~/shared/providers/Namespace';
import { getErrorState } from '~/shared/utils/error-utils';
import { TektonResourceLabel } from '~/types';
import { downloadYamlAction } from '~/utils/common-utils';
import { isResourceEnterpriseContract } from '~/utils/conforma-utils';
import { getDisplayNameFromChildReferences, taskRunStatus } from '~/utils/pipeline-utils';

export const TaskRunDetailsView: React.FC = () => {
  const { taskRunName } = useParams<RouterParams>();
  const namespace = useNamespace();
  const applicationBreadcrumbs = useApplicationBreadcrumbs();
  const params = useParams();
  const navigate = useNavigate();
  const [taskRun, loaded, error] = useTaskRunV2(namespace, taskRunName);

  const trStatus = React.useMemo(
    () => (loaded && taskRun && !error ? taskRunStatus(taskRun) : null),
    [loaded, taskRun, error],
  );

  useStatusOnFavicon(trStatus);

  const applicationName =
    params.applicationName || taskRun?.metadata?.labels?.[PipelineRunLabel.APPLICATION];
  const baseURL = TASKRUN_DETAILS_PATH.createPath({
    applicationName,
    workspaceName: namespace,
    taskRunName,
  });
  const { activeTab } = params;

  React.useEffect(() => {
    if (!activeTab && trStatus) {
      trStatus === runStatus.Succeeded ? navigate(`${baseURL}`) : navigate(`${baseURL}/logs`);
    }
  }, [activeTab, baseURL, navigate, trStatus]);

  const plrName = taskRun?.metadata?.labels?.[TektonResourceLabel.pipelinerun];
  const [pipelineRun] = usePipelineRunV2(namespace, plrName || '');
  const taskDisplayName = getDisplayNameFromChildReferences(pipelineRun, taskRunName);

  if (error) {
    return getErrorState(error, loaded, 'task run');
  }

  if (!loaded) {
    return (
      <Bullseye>
        <Spinner />
      </Bullseye>
    );
  }

  const showSecurityTab =
    isResourceEnterpriseContract(taskRun) ||
    taskRun.metadata?.labels?.[TektonResourceLabel.pipelineTask] === CONFORMA_TASK;

  const showVulnerabilitiesTab =
    taskRun.metadata?.labels?.[TektonResourceLabel.pipelineTask] === ROXCTL_SCAN_TASK;

  return (
    <DetailsPage
      headTitle={taskRunName}
      breadcrumbs={[
        ...applicationBreadcrumbs,
        {
          path: PIPELINERUN_LIST_PATH.createPath({ applicationName, workspaceName: namespace }),
          name: 'Pipeline runs',
        },
        {
          path: PIPELINERUN_DETAILS_PATH.createPath({
            applicationName,
            workspaceName: namespace,
            pipelineRunName: plrName,
          }),
          name: plrName,
        },
        {
          path: PIPELINERUN_TASK_LIST.createPath({
            applicationName,
            workspaceName: namespace,
            pipelineRunName: plrName,
          }),
          name: `Task runs`,
        },
        {
          path: TASKRUN_DETAILS_PATH.createPath({
            applicationName,
            workspaceName: namespace,
            taskRunName,
          }),
          name: taskRunName,
        },
      ]}
      title={
        <>
          <FeatureFlagIndicator flags={['taskruns-kubearchive']} />
          <span className="pf-v6-u-mr-sm">
            {taskRunName}
            {taskDisplayName ? ` (${taskDisplayName})` : ''}
          </span>
          <StatusIconWithTextLabel status={trStatus} />
        </>
      }
      actions={[createDetailsPageAction(downloadYamlAction(taskRun))]}
      baseURL={baseURL}
      tabs={[
        {
          key: 'index',
          label: 'Details',
        },
        {
          key: 'logs',
          label: 'Logs',
          isFilled: true,
        },
        ...(showSecurityTab
          ? [
              {
                key: 'security',
                label: 'Security',
              },
            ]
          : []),
        ...(showVulnerabilitiesTab
          ? [
              {
                key: 'vulnerabilities',
                label: 'Vulnerabilities',
              },
            ]
          : []),
      ]}
    />
  );
};

export default TaskRunDetailsView;
